import type { FullMapModel } from 'src/app/models/form-common/full-map.model';
import type { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import type { ParcelasEnvironmentDefinition } from '../environment-variables';
import type {
    ParcelasDropdownDataSourceFactory,
    SourceAwareParcelasCatalogRow
} from './parcelas-dropdown-datasource.factory';

export type ParcelasMapCatalogSelection = {
    readonly id: string;
    readonly label: string;
};

export type ParcelasMapCatalogSelections = {
    readonly cultivo: ParcelasMapCatalogSelection;
    readonly variedad: ParcelasMapCatalogSelection;
};

export type ParcelasMapCatalogContext = {
    readonly dataSourceFactory: ParcelasDropdownDataSourceFactory;
    readonly formModel: Partial<ParcelasModel> | null | undefined;
    readonly fallbackModel: Partial<ParcelasModel> | null | undefined;
    readonly currentCultivoValue: unknown;
    readonly currentVariedadValue: unknown;
    readonly selectedCultivo: SourceAwareParcelasCatalogRow | null;
    readonly selectedVariedad: SourceAwareParcelasCatalogRow | null;
    readonly legacyCultivoPayloadValue: unknown;
    readonly legacyVariedadPayloadValue: unknown;
};

export type ParcelasIncomingMapData = {
    readonly provincia?: unknown;
    readonly municipio?: unknown;
    readonly poligono?: unknown;
    readonly parcela?: unknown;
    readonly recinto?: unknown;
    readonly refpar?: unknown;
    readonly usoSigpac?: unknown;
    readonly modo?: unknown;
    readonly ha?: unknown;
    readonly superficieSigpac?: unknown;
    readonly superficieCultivada?: unknown;
    readonly coord?: unknown;
    readonly color?: unknown;
    readonly cultivoNombre?: unknown;
    readonly variedadNombre?: unknown;
    readonly nombreParcela?: unknown;
};

export type ParcelasIncomingMapContext = {
    readonly model: Partial<ParcelasModel>;
    readonly currentMap: FullMapModel | null | undefined;
    readonly data: ParcelasIncomingMapData;
    readonly env: ParcelasEnvironmentDefinition;
    readonly userId: string;
    readonly catalogContext: ParcelasMapCatalogContext;
    readonly shouldUpdateName: boolean;
};

export type ParcelasIncomingMapResult = {
    readonly modelPatch: Partial<ParcelasModel>;
    readonly map: FullMapModel;
    readonly suggestedName: string;
};

export type ParcelasSubmittedMapContext = {
    readonly map: FullMapModel;
    readonly backendParcel: Partial<ParcelasModel>;
    readonly hasClients: boolean;
    readonly userId: string;
    readonly catalogContext: ParcelasMapCatalogContext;
};

/**
 * Owns Parcelas map data shaping so the Angular component does not need catalog and map mutation rules inline.
 */
export class ParcelasMapAdapter {

    private static readonly defaultMapColor = '#88dd66';

    public static resolveCatalogSelections(context: ParcelasMapCatalogContext): ParcelasMapCatalogSelections {
        return {
            cultivo: this.resolveCultivoSelection(context),
            variedad: this.resolveVariedadSelection(context),
        };
    }

    public static resolveCultivoSelection(context: ParcelasMapCatalogContext): ParcelasMapCatalogSelection {
        const parsedValue = context.dataSourceFactory.parseCatalogKey(context.currentCultivoValue);
        const id = context.dataSourceFactory.resolveFirstCatalogValue(
            context.legacyCultivoPayloadValue,
            context.selectedCultivo?.codigo,
            context.selectedCultivo?.codigo_cultivo,
            context.selectedCultivo?.id,
            parsedValue.primaryCode,
            context.currentCultivoValue,
        );
        const label = context.dataSourceFactory.resolveFirstCatalogValue(
            context.selectedCultivo?.cultivo,
            context.formModel?.cultivo,
            context.formModel?.cultivo_legacy,
            context.fallbackModel?.cultivo,
            context.fallbackModel?.cultivo_legacy,
        );

        return { id, label };
    }

    public static resolveVariedadSelection(context: ParcelasMapCatalogContext): ParcelasMapCatalogSelection {
        const parsedValue = context.dataSourceFactory.parseCatalogKey(context.currentVariedadValue);
        const id = context.dataSourceFactory.resolveFirstCatalogValue(
            context.legacyVariedadPayloadValue,
            context.selectedVariedad?.codigo_variedad,
            context.selectedVariedad?.id_variedad,
            context.selectedVariedad?.id,
            parsedValue.secondaryCode,
            context.currentVariedadValue,
        );
        const label = context.dataSourceFactory.resolveFirstCatalogValue(
            context.selectedVariedad?.variedad,
            context.formModel?.variedad,
            context.fallbackModel?.variedad,
        );

        return { id, label };
    }

    public static withColor(
        map: FullMapModel,
        context: ParcelasMapCatalogContext,
        customColors: Record<string, string | undefined>,
    ): FullMapModel {
        const cultivoName = this.resolveCultivoSelection(context).label;
        const customColor = cultivoName !== '' ? customColors[cultivoName] : undefined;

        return {
            ...map,
            color: customColor ?? this.defaultMapColor,
        } as FullMapModel;
    }

    public static buildIncomingMapData(context: ParcelasIncomingMapContext): ParcelasIncomingMapResult {
        const dataSourceFactory = context.catalogContext.dataSourceFactory;
        const data = context.data;
        const modelPatch = this.buildIncomingModelPatch(data, context.env, dataSourceFactory);
        const sourceModel = {
            ...context.model,
            ...modelPatch,
        } as ParcelasModel;
        const selections = this.resolveCatalogSelections(context.catalogContext);
        const map = {
            ...(context.currentMap ?? {} as FullMapModel),
            id_finca: sourceModel.id_finca ?? '',
            id_sector: sourceModel.id_sector ?? '',
            id_cultivo: selections.cultivo.id,
            id_cultivo_legacy: selections.cultivo.id,
            id_variedad: selections.variedad.id,
            id_variedad_legacy: selections.variedad.id,
            cultivo: selections.cultivo.label || dataSourceFactory.toStringId(data.cultivoNombre),
            variedad: selections.variedad.label || dataSourceFactory.toStringId(data.variedadNombre),
            id_usuario: context.userId,
            coordenadas: dataSourceFactory.toStringId(data.coord),
            color: dataSourceFactory.toStringId(data.color),
        } as FullMapModel;

        map.cultivo_legacy = map.cultivo;

        return {
            modelPatch,
            map,
            suggestedName: context.shouldUpdateName ? this.resolveIncomingMapName(data, context.env) : '',
        };
    }

    public static buildSubmittedMapData(context: ParcelasSubmittedMapContext): FullMapModel {
        const dataSourceFactory = context.catalogContext.dataSourceFactory;
        const selections = this.resolveCatalogSelections(context.catalogContext);
        const backendParcel = context.backendParcel;
        const map = {
            ...context.map,
            id_parcela: dataSourceFactory.toStringId(backendParcel.id),
            id_finca: dataSourceFactory.toStringId(backendParcel.id_finca),
            id_cultivo: dataSourceFactory.toStringId(backendParcel.id_cultivo ?? selections.cultivo.id),
            id_variedad: dataSourceFactory.toStringId(backendParcel.id_variedad ?? selections.variedad.id),
            cultivo: selections.cultivo.label,
            variedad: selections.variedad.label,
            id_sector: dataSourceFactory.toStringId(backendParcel.id_sector),
            id_usuario: context.userId,
        } as FullMapModel;

        map.id_cultivo_legacy = map.id_cultivo;
        map.id_variedad_legacy = map.id_variedad;
        map.cultivo_legacy = selections.cultivo.label;

        if (context.hasClients) {
            map.id_cliente = dataSourceFactory.toStringId(backendParcel.id_cliente);
        }

        return map;
    }

    private static buildIncomingModelPatch(
        data: ParcelasIncomingMapData,
        env: ParcelasEnvironmentDefinition,
        dataSourceFactory: ParcelasDropdownDataSourceFactory,
    ): Partial<ParcelasModel> {
        if (env.appName === 'amoros') {
            return {};
        }

        const patch: Partial<ParcelasModel> = {
            cod_provincia: dataSourceFactory.toStringId(data.provincia),
            municipio: dataSourceFactory.toStringId(data.municipio),
            poligono: dataSourceFactory.toStringId(data.poligono),
            numero: dataSourceFactory.toStringId(data.parcela),
            recinto: dataSourceFactory.toStringId(data.recinto),
            sigpac: dataSourceFactory.toStringId(data.refpar),
            uso_sigpac: dataSourceFactory.toStringId(data.usoSigpac),
        };

        const superficieSigpac = dataSourceFactory.toStringId(data.superficieSigpac ?? data.ha);
        const superficieCultivada = dataSourceFactory.toStringId(data.superficieCultivada ?? data.ha);

        if (data.modo === 'sigpac') {
            patch.superficie_sigpac = superficieSigpac;
            patch.superficie_cultivada = superficieCultivada || superficieSigpac;
        } else if (data.modo === 'manual') {
            patch.superficie_cultivada = superficieCultivada;
        }

        return patch;
    }

    private static resolveIncomingMapName(data: ParcelasIncomingMapData, env: ParcelasEnvironmentDefinition): string {
        const nombreParcela = typeof data.nombreParcela === 'string' ? data.nombreParcela.trim() : '';

        if (nombreParcela !== '') {
            return nombreParcela;
        }

        const parcela = typeof data.parcela === 'string' ? data.parcela.trim() : '';

        return parcela !== '' ? `${env.parcelaName} ${parcela}` : '';
    }
}
